// Phase 13 — description-aware symbol search.
//
// Powered by the prebuilt `SEARCH_INDEX` (one entry per indexed symbol) so
// every keystroke is cheap, deterministic, and works offline. Scoring is
// intentionally simple and bag-of-words rather than vector — it has to work
// for queries like "arrow pointing left with hook" → ↩ (LEFTWARDS ARROW
// WITH HOOK) without ML, by matching as many query tokens as possible
// against name + alias + block + prose tokens.

import type { SearchEntry } from "@/data/search-index.generated";

export type { SearchEntry } from "@/data/search-index.generated";

// The search index (~800 KB) is loaded lazily — on first interaction with a
// search box — so it never sits in the homepage's critical bundle. searchSymbols
// returns [] until loadSearchIndex() has resolved; callers gate on that.
let SEARCH_INDEX: readonly SearchEntry[] | null = null;
let loadPromise: Promise<void> | null = null;

export function isSearchIndexReady(): boolean {
  return SEARCH_INDEX !== null;
}

export function loadSearchIndex(): Promise<void> {
  if (SEARCH_INDEX) return Promise.resolve();
  if (!loadPromise) {
    loadPromise = import("@/data/search-index.generated").then((m) => {
      SEARCH_INDEX = m.SEARCH_INDEX;
    });
  }
  return loadPromise;
}

const STOPWORDS = new Set([
  "the","a","an","and","or","of","to","in","on","is","it","as","by","for",
  "with","this","that","from","its","be","are","was","were","also","into","at",
  "any","all","one","two","sign","symbol","character","point","points",
]);

function normalize(s: string): string {
  return s.toLowerCase().normalize("NFKD").replace(/[\u0300-\u036f]/g, "");
}

/** Lightweight tokenizer — mirrors the build script's stopword filter so the
 *  same word boundaries apply on both sides. Keeps very short tokens too so
 *  numeric / code queries ("u+2665", "26") still hit. */
export function tokenizeQuery(q: string): string[] {
  return normalize(q)
    .split(/[^a-z0-9+]+/)
    .filter((t) => t.length > 0 && !STOPWORDS.has(t));
}

/** "u+2665" / "u2665" / "2665" / "U+2665 U+FE0F" all normalised to the same
 *  whitespace-joined "u+XXXX" form used in `entry.code`. */
function normaliseCodeQuery(q: string): string | null {
  const cleaned = q.trim().toLowerCase();
  if (!cleaned) return null;
  const hits = Array.from(cleaned.matchAll(/u?\+?([0-9a-f]{2,6})/g)).map((m) => m[1]);
  if (hits.length === 0) return null;
  return hits.map((h) => `u+${h.padStart(4, "0")}`).join(" ");
}

interface Scored {
  entry: SearchEntry;
  score: number;
}

const NAME_EXACT = 1000;
const SYMBOL_EXACT = 1200;
const NAME_PREFIX = 500;
const NAME_CONTAINS = 240;
const CODE_HIT = 700;
const TOKEN_EXACT = 30;
const TOKEN_PREFIX = 18;
const TOKEN_SUBSTRING = 8;
const ALL_TOKENS_BONUS = 60;
const NAME_TOKEN_BONUS = 12; // matched against the *display* name specifically

/**
 * Search all symbols by description, name, alias, codepoint, or raw character.
 * Returns top `limit` results ordered by descending score, ties broken by name.
 */
export function searchSymbols(rawQuery: string, limit = 8): SearchEntry[] {
  const trimmed = rawQuery.trim();
  if (!trimmed || !SEARCH_INDEX) return [];

  // 1) Exact character match — always wins.
  const direct = SEARCH_INDEX.find((e) => e.symbol === trimmed);

  const qLower = normalize(trimmed);
  const qTokens = tokenizeQuery(trimmed);
  const codeQuery = normaliseCodeQuery(trimmed);

  const scored: Scored[] = [];

  for (const entry of SEARCH_INDEX) {
    let score = 0;

    if (entry.symbol === trimmed) score += SYMBOL_EXACT;

    const nameLower = normalize(entry.name);
    if (nameLower === qLower) score += NAME_EXACT;
    else if (nameLower.startsWith(qLower)) score += NAME_PREFIX;
    else if (qLower.length >= 3 && nameLower.includes(qLower)) score += NAME_CONTAINS;

    if (codeQuery && entry.code.includes(codeQuery)) score += CODE_HIT;

    if (qTokens.length > 0) {
      let matched = 0;
      for (const qt of qTokens) {
        let hit = 0;
        // Display-name tokens earn a small extra weight so name matches beat
        // matches that only show up in the prose corpus.
        for (const nt of entry.nameTokens) {
          if (nt === qt) { hit = Math.max(hit, TOKEN_EXACT + NAME_TOKEN_BONUS); break; }
          if (qt.length >= 3 && nt.startsWith(qt)) hit = Math.max(hit, TOKEN_PREFIX + NAME_TOKEN_BONUS);
          else if (qt.length >= 3 && nt.includes(qt)) hit = Math.max(hit, TOKEN_SUBSTRING + NAME_TOKEN_BONUS);
        }
        for (const t of entry.tokens) {
          if (t === qt) { hit = Math.max(hit, TOKEN_EXACT); break; }
          if (qt.length >= 3 && t.startsWith(qt)) hit = Math.max(hit, TOKEN_PREFIX);
          else if (qt.length >= 3 && t.includes(qt)) hit = Math.max(hit, TOKEN_SUBSTRING);
        }
        if (hit > 0) {
          matched++;
          score += hit;
        }
      }
      // Reward entries that cover every meaningful query token (fuzzy-AND).
      if (matched === qTokens.length && qTokens.length > 1) {
        score += ALL_TOKENS_BONUS * qTokens.length;
      }
    }

    if (score > 0) scored.push({ entry, score });
  }

  scored.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    // Stable secondary sort: shorter names first (more specific), then alpha.
    if (a.entry.name.length !== b.entry.name.length) {
      return a.entry.name.length - b.entry.name.length;
    }
    return a.entry.name.localeCompare(b.entry.name);
  });

  // If the user literally typed the symbol but nothing else scored higher,
  // make sure that exact match is at position 0.
  if (direct && scored[0]?.entry !== direct) {
    const idx = scored.findIndex((s) => s.entry === direct);
    if (idx > 0) {
      const [pulled] = scored.splice(idx, 1);
      scored.unshift(pulled);
    } else if (idx === -1) {
      scored.unshift({ entry: direct, score: SYMBOL_EXACT });
    }
  }

  return scored.slice(0, limit).map((s) => s.entry);
}
